import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import BotKnowledgeManagerAPI from '../../api/wgpt/botKnowledgeManager';
import { throwErrorMessage } from '../utils/api';
import getUuid from 'widget/helpers/uuid';

export const state = {
  records: [],
  knowledge: '',
  botId: null,
  uiFlags: {
    isFetching: false,
    isSending: false,
    isUpdating: false,
    isAvailable: true,
    error: null,
  },
};

export const getters = {
  getMessages($state) {
    return $state.records;
  },
  getKnowledge($state) {
    return $state.knowledge;
  },
  getCurrentKnowledge($state) {
    for (let i = $state.records.length - 1; i >= 0; i -= 1) {
      const msg = $state.records[i];
      if (msg.role === 'activity') {
        return msg.content.content;
      }
    }
    return $state.knowledge;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

const addMessages = (commit, messages) => {
  messages.forEach(message => {
    commit(types.ADD_WGPT_KNOWLEDGE_MANAGER_MESSAGE, {
      ...message,
      id: getUuid(),
    });
  });
};

export const actions = {
  get: async ({ commit }, botId) => {
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_BOT_ID, botId);
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, { isFetching: true });
    try {
      const api = new BotKnowledgeManagerAPI(botId);
      const { data } = await api.state();

      const messages = (data.messages || []).map(msg => ({
        ...msg,
        id: getUuid(),
      }));
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_MESSAGES, messages);
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_KNOWLEDGE, data.knowledge || '');
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
        isAvailable: true,
        isFetching: false,
      });
    } catch (error) {
      const uiFlagsUpdates = { isFetching: false };
      if (error?.response?.data?.error === 'NOT_AVAILABLE') {
        uiFlagsUpdates.isAvailable = false;
      }
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, uiFlagsUpdates);
      throwErrorMessage(error);
    }
  },
  chat: async ({ commit }, { botId, content }) => {
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: true,
      error: null,
    });
    addMessages(commit, [{ pending: true, role: 'user', content }]);
    try {
      const api = new BotKnowledgeManagerAPI(botId);
      await api.converse({ content });
      // 202 Accepted — messages arrive via the wgpt_knowledge_manager.completed event.
    } catch (error) {
      commit(types.REMOVE_WGPT_KNOWLEDGE_MANAGER_PENDING_MESSAGES);
      const code = error?.response?.status === 409 ? 'processing' : 'failed';
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
        isSending: false,
        error: code,
      });
    }
  },
  persist: async ({ commit, getters: moduleGetters }, botId) => {
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, { isUpdating: true });
    try {
      const api = new BotKnowledgeManagerAPI(botId);
      await api.persist();

      commit(
        types.SET_WGPT_KNOWLEDGE_MANAGER_KNOWLEDGE,
        moduleGetters.getCurrentKnowledge
      );
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, { isUpdating: false });
    }
  },
  rollback: async ({ commit }, { botId, version }) => {
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: true,
      error: null,
    });
    try {
      const api = new BotKnowledgeManagerAPI(botId);
      await api.rollback({ rollback_version: version });
      // 202 Accepted — messages arrive via the wgpt_knowledge_manager.completed event.
    } catch (error) {
      const code = error?.response?.status === 409 ? 'processing' : 'failed';
      commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
        isSending: false,
        error: code,
      });
    }
  },
  onConverseCompleted: ({ commit, state: $state }, { botId, messages }) => {
    if ($state.botId !== botId) return;
    const records = (messages || []).map(message => ({
      ...message,
      id: getUuid(),
    }));
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_MESSAGES, records);
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: false,
      error: null,
    });
  },
  onConverseFailed: ({ commit, state: $state }, { botId }) => {
    if ($state.botId !== botId) return;
    commit(types.REMOVE_WGPT_KNOWLEDGE_MANAGER_PENDING_MESSAGES);
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: false,
      error: 'failed',
    });
  },
  handleTimeout: ({ commit }) => {
    commit(types.REMOVE_WGPT_KNOWLEDGE_MANAGER_PENDING_MESSAGES);
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: false,
      error: 'timeout',
    });
  },
  resync: async ({ commit, dispatch }, botId) => {
    await dispatch('get', botId);
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, {
      isSending: false,
      error: null,
    });
  },
  clearError: ({ commit }) => {
    commit(types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG, { error: null });
  },
  reset: async ({ dispatch }, botId) => {
    try {
      const api = new BotKnowledgeManagerAPI(botId);
      await api.reset();
      await dispatch('get', botId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};

export const mutations = {
  [types.SET_WGPT_KNOWLEDGE_MANAGER_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_WGPT_KNOWLEDGE_MANAGER_MESSAGES]: MutationHelpers.set,
  [types.ADD_WGPT_KNOWLEDGE_MANAGER_MESSAGE]($state, message) {
    $state.records.forEach(record => {
      if (!record.pending) return;
      MutationHelpers.destroy($state, record.id);
    });
    MutationHelpers.create($state, message);
  },
  [types.SET_WGPT_KNOWLEDGE_MANAGER_KNOWLEDGE]($state, knowledge) {
    $state.knowledge = knowledge;
  },
  [types.SET_WGPT_KNOWLEDGE_MANAGER_BOT_ID]($state, botId) {
    $state.botId = botId;
  },
  [types.REMOVE_WGPT_KNOWLEDGE_MANAGER_PENDING_MESSAGES]($state) {
    $state.records
      .filter(record => record.pending)
      .forEach(record => MutationHelpers.destroy($state, record.id));
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
